import React, { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import moment from 'moment';
import { difference, capitalize, toLower, uniqueId, startCase, camelCase } from 'lodash';
import { ui, uiA, uiIconSvg, uiButton, uiMerge } from 'envy-ui';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery, useMutation } from '@apollo/client';
// import Countdown, { calcTimeDelta } from 'react-countdown';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
import { GENERAL_SETTINGS, LAST_SYNC_DATE, SYNC_PAGE_DATA, SET_CLIENT_STATE } from '../../graphqls';
import { Dropdown } from './inputControls';
import constants from '../../constants.json';
import getScheduledInfo from '../../util/getScheduledInfo';

export default function MainHeader({ stopPolling, startPolling }) {
  const { moment: momentConfig } = constants;
  const [withError, setWithError] = useState(false);
  const history = useHistory();
  const { pathname } = useLocation();
  const {
    loading: loadingGeneralSettings,
    data: {
      generalSettings: {
        syncStartOn,
        interfaceAddress = '',
        accountEmail,
        subDomain,
        syncStatus,
        publishedAt,
        newerUpdatesAvailable,
        syncRequestedAt,
        planName,
        billable,
        syncSettings: {
          autoSync = false,
          frequency,
          amPmGenerated,
          timeGenerated,
          weeklyEvery,
          weeklyOn,
          monthlyMode,
          monthlyEvery,
          monthlyEachDates,
          monthlyOnTheDayOption,
          monthlyOnTheFirstLastOption,
          reportingDateBeforeAfter,
          reportingDateDays,
        } = {},
        reportingDates = '',
      } = {},
    } = {},
  } = useQuery(GENERAL_SETTINGS, {
    onCompleted({ generalSettings: { syncStatus: syncing } }) {
      if (syncing) {
        startPolling(3000);
      }
    },
    onError(error) {
      console.error('error', error);
      window.location.href = 'https://envisio.app.envisio.com/404.html';
    },
  });
  const { loading: loadingLastSyncDate, data: { lastSyncDate: { createdAt, appliedAt } = {} } = {} } = useQuery(LAST_SYNC_DATE, {
    onCompleted: ({ lastSyncDate: { createdAt, receivedAt } }) => {
      if (!receivedAt && syncStatus && moment.duration(moment().diff(moment(createdAt))).asDays() >= 1) {
        setWithError(true);
      }
    },
  });
  const { loading: loadingSyncPageData, data: { syncPageData = [] } = {} } = useQuery(SYNC_PAGE_DATA);
  const [setState] = useMutation(SET_CLIENT_STATE);
  const lastPublishDate = publishedAt ? moment(publishedAt).format('MMM D, YYYY, h:mm:ss a') : 'Never';
  const newerUpdatesLabel = newerUpdatesAvailable ? 'There are unpublished changes' : '';
  const lastSyncLabel = syncStatus ? moment(syncRequestedAt).format('MMM D, YYYY, h:mm:ss a') : '';
  const syncClass = syncStatus ? ' disabled' : '';

  useEffect(() => {
    if (!syncStatus) {
      stopPolling();
    }
  }, [syncStatus]);

  const onToggleGeneralSettings = () => setState({ variables: { sideMenu: 'generalSettingsView' } });
  const onTogglePreviewPublishSettings = () => setState({ variables: { sideMenu: 'PreviewPublishDropdown' } });
  const onSync = () => {
    if (!syncStatus && syncPageData.length) {
      history.push('/sync');
    } else if (!syncStatus || withError) {
      setState({ variables: { modalTypeShown: 'requestSync' } });
    }
  };

  const autosyncStatus = {
    on: { tooltip: 'Autosync is ON', iconModifier: '--autosync-on' },
    off: { tooltip: 'Autosync is OFF', iconModifier: '--autosync-off' },
    error: { tooltip: 'Autosync Error', iconModifier: '--autosync-error' },
  };
  let syncButtonLabel = 'Request Sync';

  if (!withError) {
    if (syncStatus) {
      syncButtonLabel = 'Sync in Progress...';
    } else if (syncPageData.length) {
      syncButtonLabel = 'View Synced Data';
    }
  }

  if (loadingGeneralSettings || loadingLastSyncDate || loadingSyncPageData) {
    return '';
  }

  let nextMoment = moment();
  let humanizedString = '';
  ({ nextMoment, humanizedString } = getScheduledInfo({
    startOn: syncStartOn,
    amPmGenerated,
    frequency,
    monthlyMode,
    monthlyEvery,
    monthlyEachDates,
    monthlyOnTheDayOption,
    monthlyOnTheFirstLastOption,
    reportingDateDays,
    reportingDateBeforeAfter,
    timeGenerated,
    weeklyEvery,
    weeklyOn,
    reportingDates,
  }));

  return (
    <div className="app-header">
      <ul {...ui([uiA`m-right-40`, 'pd-status'])}>
        <li data-tooltip-content={newerUpdatesLabel} data-tooltip-place="bottom" style={{ fontSize: '15px' }}>
          {newerUpdatesAvailable && <FontAwesomeIcon aria-hidden="true" icon={['fal', 'exclamation-triangle']} color="orange" />}
          <span style={{ lineHeight: '20px', fontSize: '11px' }}>
            <strong>Last Published:</strong>
            {lastPublishDate}
          </span>
        </li>
        {!!autoSync && (
          <li>
            <button {...ui([uiButton`--clean --small`])} data-tooltip-id="next-sync-id" data-tooltip-content={`Next sync: ${moment().diff(nextMoment) > 604800000 ? nextMoment.format(momentConfig.short) : nextMoment.fromNow()}`} onClick={onSync}>
              <div {...ui([uiButton`__content`, uiA`f text-align-left font-size-13 p-x-small color-nobel:${!autoSync}`])} tabIndex={-1}>
                <div {...ui([uiIconSvg`#${autosyncStatus.on.iconModifier}`, uiA`m-right-3`])} />
                <span {...ui([uiA`font-size-11 top-1- m-left-none m-right-small`])}>
                  <span {...ui([uiA`semibold m-left-none top-0`])}>Next Sync:&nbsp;</span>
                  {moment().diff(nextMoment) > 604800000 ? nextMoment.format(momentConfig.short) : nextMoment.fromNow()}
                </span>
                <Tooltip id="next-sync-id" delayShow={500}>
                  <span {...ui([uiA`text-align-left m-left-none`])}>
                    {autosyncStatus.on.tooltip}
                    <br />
                    <FroalaEditorView
                      model={`Next run: ${nextMoment?.format('lll')}<br/>${startCase(camelCase(frequency))}${
                        humanizedString ? ` ${humanizedString}` : ''
                      }`}
                    />
                  </span>
                </Tooltip>
              </div>
            </button>
          </li>
        )}
        {withError ? (
          <li style={{ fontSize: '15px' }} data-tooltip-content="Sync failed" data-tooltip-place="bottom">
            <FontAwesomeIcon icon={['fal', 'sync']} color="red" />
            <span data-tooltip-content={lastSyncLabel} data-tooltip-place="bottom" style={{ lineHeight: '20px', fontSize: '11px' }}>
              <strong>Last Sync: </strong>
              {moment(createdAt).format('MMM D, YYYY, h:mm:ss a')}
            </span>
          </li>
        ) : (
          <li
            style={{ fontSize: '15px' }}
            data-tooltip-content={!appliedAt && moment(createdAt).diff(moment(), 'days') >= 1 ? 'Synced data has not been applied.' : ''}
            data-tooltip-place="bottom"
          >
            {!appliedAt && moment(createdAt).diff(moment(), 'days') >= 1 ? <FontAwesomeIcon icon={['fal', 'sync']} color="orange" /> : ''}
            {!!syncStatus && <FontAwesomeIcon icon={['fal', 'sync']} spin />}
            <span data-tooltip-content={lastSyncLabel} data-tooltip-place="bottom" style={{ lineHeight: '20px', fontSize: '11px' }}>
              <strong>Last Sync: </strong>
              {syncStatus ? 'Sync in Progress' : moment(createdAt).format('MMM D, YYYY, h:mm:ss a')}
            </span>
          </li>
        )}
      </ul>
      <Tooltip />
      <Link to="/home/edit" className="hollow-btn header-title" href="homepage">
        <h1 className="header-title__title">
          <FontAwesomeIcon icon={['fal', 'browser']} />
          <span>Public Dashboard</span>
        </h1>
        <span className="header-title__divider">|</span>
        <span className="header-title__subtitle">{planName}</span>
      </Link>
      {pathname !== '/sync' && (
        <Dropdown
          defaultValue={<FontAwesomeIcon icon={['fal', 'sliders-h']} id="systemSettings" />}
          customClass={uiMerge([uiA`w-25 border-left-panel`, 'system-setting-btn'])}
        >
          <button type="button" className="hollow-btn" onClick={onToggleGeneralSettings}>
            <FontAwesomeIcon icon={['fal', 'cog']} />
            <span>Settings</span>
          </button>
          <button type="button" className="hollow-btn" disabled={pathname === '/sync'} onClick={onTogglePreviewPublishSettings}>
            <FontAwesomeIcon icon={['fal', 'globe-americas']} />
            <span>Preview/Publish</span>
          </button>
          <button type="button" className={`hollow-btn${syncClass}`} disabled={pathname === '/sync'} onClick={onSync}>
            <FontAwesomeIcon icon={['fal', 'sync']} />
            <span>{syncButtonLabel}</span>
          </button>
        </Dropdown>
      )}
    </div>
  );
}
